import { SourceTypeResponse } from "api/company";
import { useConfig } from "contexts/ConfigContext";
import merge from "lodash.merge";
import { useMemo } from "react";
import { sourceTypeFileName, sourceTypeSelectionText } from "utils";

import { routes } from "config/urls";

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

type SourceTypeDisplayProps = {
  revisit: {
    message: string;
    linkUrl: string;
    linkText: string;
  };
  error: {
    tryAgainMessage: string;
  };
  link: {
    warningTitle: string;
    warningMessage: string;
    sourceText: string;
    fileUploadText: string;
    fileUploadSubtext: string;
    canAddAnother: boolean;
    canUpload: boolean;
  };
};

export const useSourceTypeDisplayProps = (
  currentSourceType?: SourceTypeResponse
) => {
  const { state } = useConfig();
  const enableFileUpload = state.data.enableFileUpload;
  return useMemo((): SourceTypeDisplayProps => {
    if (!currentSourceType) {
      return {
        revisit: {
          message: "Great news, you already have a package connected",
          linkUrl: routes.company.link,
          linkText: "",
        },
        error: {
          tryAgainMessage: "Try again",
        },
        link: {
          warningTitle: "",
          warningMessage: "",
          sourceText: "Select your platform",
          fileUploadText: "Upload your files",
          fileUploadSubtext: "You can upload your files below",
          canAddAnother: true,
          canUpload: false,
        },
      };
    }

    const sourceTypeName = currentSourceType.sourceType.toLowerCase();
    const article = /^[aeiou]/.test(sourceTypeName) ? "an" : "a";

    const defaultDisplayProps: SourceTypeDisplayProps = {
      revisit: {
        message: `Great news, you've already connected all ${sourceTypeName} packages`,
        linkUrl: currentSourceType.useCustomIntro
          ? routes.company.customIntro
          : routes.company.link,
        linkText: "Make changes to your connection",
      },
      error: {
        tryAgainMessage:
          currentSourceType &&
          (currentSourceType.maxConnections === undefined ||
            currentSourceType.maxConnections > currentSourceType.connected + 1)
            ? "Try again or add another"
            : "Try again",
      },
      link: {
        warningTitle: "Warning",
        warningMessage: `You already have ${article} ${sourceTypeName} package connected. Selecting a different ${sourceTypeName} package will terminate your existing ${sourceTypeName} package connection, and could result in invalid data being synced.`,
        sourceText: sourceTypeSelectionText[currentSourceType.sourceType],
        fileUploadText: `Upload your ${
          sourceTypeFileName[currentSourceType.sourceType]
        }`,
        fileUploadSubtext: `You can upload your ${
          sourceTypeFileName[currentSourceType.sourceType]
        } below`,
        canAddAnother: currentSourceType
          ? currentSourceType.maxConnections === undefined ||
            (currentSourceType.maxConnections > 0 &&
              currentSourceType.connected < currentSourceType.maxConnections)
          : false,
        canUpload: false,
      },
    };

    const computeProps = (
      overridePartialProps: DeepPartial<SourceTypeDisplayProps>
    ) => {
      return merge({}, defaultDisplayProps, overridePartialProps);
    };

    switch (currentSourceType.sourceType) {
      case "Accounting":
        return computeProps({
          revisit: {
            message:
              "Great news, you already have an accounting package connected",
          },
          link: {
            canUpload: enableFileUpload?.accounting,
          },
        });
      case "Banking":
        return computeProps({
          revisit: {
            message:
              "You already have a bank connected through the selected banking platform.",
            linkText: "Add another bank",
          },
          link: {
            warningMessage:
              "You already have a bank connected. Make sure you aren't trying to connect the same bank account again. Otherwise you may create several copies of the same financial data.",
            warningTitle: "Warning",
            canUpload: enableFileUpload?.banking,
          },
        });
      case "Commerce":
        return computeProps({
          revisit: {
            message:
              "You already have an account connected through the selected commerce platform.",
            linkText: "Add another account",
          },
          link: {
            canUpload: enableFileUpload?.commerce,
          },
        });
      case "Other":
        return computeProps({
          revisit: {
            message: "You have already uploaded your business documents",
            linkText: "Upload more business documents",
          },
          link: {
            canUpload: enableFileUpload?.businessDocuments,
          },
        });
    }
  }, [currentSourceType, enableFileUpload]);
};
